/**
 * What the canonical roster sounds like, and what it *should* sound like.
 *
 * `SfxName` lives in `core/types.ts` and the synthesis lives in `audio/sfx.ts`,
 * neither of which belongs to the entities. So the five new kinds borrow from
 * the existing pool, and every borrow is recorded here beside the sound it is
 * standing in for. One table rather than a literal buried in each behaviour, so
 * that when the real voices land the swap is five lines in one file and nothing
 * has to be hunted for.
 *
 * The borrows are chosen to be *wrong in the right direction* — a bandpassed
 * wordless voice standing in for a scream reads as a scream heard through a
 * wall, which is at least the same family. Nothing borrows `hound-growl`,
 * `hound-scream` or `hound-step`: those three are the game's one taught
 * warning, and a player who has learned that a growl means stop moving must
 * never hear one from something that is not a hound.
 */

import type { SfxName } from '../core/types';

/**
 * The voices, keyed by what they are for rather than by kind, since two
 * entities may want the same noise for different reasons.
 *
 * The `want` note on each is the request filed against `audio/`: a new
 * `SfxName` in `core/types.ts` and a case in `audio/sfx.ts`. Until then the
 * `use` is what plays.
 */
export const VOICE = {
  /**
   * The Bacteria's scream when it sights prey and while it runs. It is the
   * entity's one warning and the only sound it makes — it moves quietly, which
   * is the documented behaviour and also the whole horror of it.
   *
   * want: `bacteria-scream` — a long ragged human-adjacent shriek, no growl in
   * it, pitched down as the chase lengthens so the acceleration is audible.
   * use: the wordless sliding-formant voice, hard and low.
   */
  bacteriaScream: 'distant-voice' as SfxName,

  /**
   * The Deathmoth's wings. Continuous while it flies, and the only way to know
   * one is coming when the flashlight is off and it is homing on the room's own
   * lights instead.
   *
   * want: `moth-wings` — dry papery flutter, a burst of four or five beats,
   * rate rising as it closes.
   * use: the smiler's breath, played fast and quiet. It is a soft noise burst
   * with a body to it, which at 1.7x reads more like a wingbeat than anything
   * else in the pool.
   */
  mothWings: 'smiler-breath' as SfxName,

  /**
   * A Partygoer's laugh during the approach, while the act is still on. High,
   * childlike and entirely friendly, which is the point.
   *
   * want: `partygoer-laugh` — several voices, not one, since they are never
   * alone. use: the smiler's laugh, played up.
   */
  partygoerLaugh: 'smiler-laugh' as SfxName,

  /**
   * The same laugh after the act drops. Played at a much lower rate, so it is
   * recognisably the sound that was charming ninety seconds ago.
   *
   * want: `partygoer-reveal`. use: as above, pitched into the floor.
   */
  partygoerReveal: 'smiler-laugh' as SfxName,

  /**
   * A pack running. Deliberately not the hound's footfall.
   *
   * Has its own name now. The synthesis is the retired stalker's mimicked
   * footstep, which is what it always was — a footfall with no teaching
   * attached to it — renamed to the only thing that plays it.
   */
  packStep: 'partygoer-step' as SfxName,

  /**
   * The Sticky-Centipede moving through water, and the wet crack of the lunge.
   *
   * Has its own name now: the retired wretch's moan, played low and slow, which
   * has the right wet body to it and no longer means anything else. A hundred
   * legs and more displaced water would be better and is a request against
   * `audio/sfx.ts`, but nothing is standing in for anything any more.
   */
  centipedeSurge: 'centipede-surge' as SfxName,
} as const;
